import React from 'react';
import { IDialogPromptSentence } from '../api/DialogPromptApi';
import { SentenceApi } from '../api/SentenceApi';
import { handleErrorMessage } from '../component/ErrorDialog';

interface UseStudentResponsePinyinProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
}

export function useStudentResponsePinyin({ history, location, setErrorMessage }: UseStudentResponsePinyinProps) {
    const [studentResponsePrompt, setStudentResponsePrompt] = React.useState("<Answer/>");
    const [studentResponsePromptErrorText, setStudentResponsePromptErrorText] = React.useState<string | null>(null);

    const [studentResponseList, setStudentResponseList] = React.useState("");
    const [studentResponseListErrorText, setStudentResponseListErrorText] = React.useState<string | null>(null);

    const [studentResponsePinyinList, setStudentResponsePinyinList] = React.useState("");
    const [studentResponsePinyinErrorText, setStudentResponsePinyinErrorText] = React.useState<string | null>(null);
    const fetchRequestIdRef = React.useRef(0);
    const validationRequestIdRef = React.useRef(0);
    const studentResponsePromptRef = React.useRef(studentResponsePrompt);

    React.useEffect(() => {
        studentResponsePromptRef.current = studentResponsePrompt;
    }, [studentResponsePrompt]);

    const validateStudentResponsePromptAndList = React.useCallback((studentResponsePrompt: string, studentResponseListList: string[]) => {
        if (studentResponseListList.length === 0) {
            setStudentResponseListErrorText("At least one response is required.");
            return;
        }

        const [prefix, suffix] = studentResponsePrompt.split("<Answer/>");

        const invalidEntry = studentResponseListList.find((res) => {
            return !(res.startsWith(prefix) && (!suffix || res.endsWith(suffix)));
        });

        if (invalidEntry) {
            setStudentResponseListErrorText(`Response "${invalidEntry}" must start with "${prefix}" and end with "${suffix}"`);
            return;
        }

        setStudentResponseListErrorText(null);
    }, []);

    const validatePinyin = React.useCallback(async (studentResponseList: string, updatedPinyin: string, requestId: number) => {
        const studentResponseListList = studentResponseList.split("\n");
        const studentResponsePinyinListList = updatedPinyin.split("\n");

        try {
            const validationResultList = await Promise.all(
                studentResponseListList.map((res, i) => {
                    const pinyinWordList = studentResponsePinyinListList[i] ? studentResponsePinyinListList[i].trim().split(/\s+/).filter(Boolean) : [];
                    return SentenceApi.isTextPinyinValid(res, pinyinWordList)
                        .then(resp => ({
                            isValid: resp.data,
                            res,
                            pinyinWords: pinyinWordList
                        }))
                        .catch(err => {
                            console.error("Validation error:", err);
                            return { isValid: false, res, pinyinWords: pinyinWordList };
                        });
                })
            );

            if (requestId !== validationRequestIdRef.current) return;
            const firstInvalid = validationResultList.find(v => !v.isValid);
            if (firstInvalid) {
                setStudentResponsePinyinErrorText(`Invalid pinyin for: "${firstInvalid.res}" → "${firstInvalid.pinyinWords.join(' ')}"`);
            } else {
                setStudentResponsePinyinErrorText(null);
            }
        } catch (err) {
            if (requestId !== validationRequestIdRef.current) return;
            handleErrorMessage(err, setErrorMessage, history, location);
            setStudentResponsePinyinErrorText("Unable to validate pinyin.");
        }
    }, [setErrorMessage, history, location]);

    const handleStudentResponsePromptChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        studentResponsePromptRef.current = value;
        setStudentResponsePrompt(value);

        const matchList = value.match(/<Answer\/>/g) || [];
        if (matchList.length !== 1) {
            setStudentResponsePromptErrorText("Prompt must contain exactly one <Answer/>");
        } else {
            setStudentResponsePromptErrorText(null);
            // read studentResponseList via ref to avoid stale closure
            const currentList = studentResponseListRef.current;
            const responseList = currentList.split("\n").map(line => line.trim()).filter(Boolean);
            validateStudentResponsePromptAndList(value, responseList);
        }
    }, [validateStudentResponsePromptAndList]);

    // keep a ref so handleStudentResponsePromptChange can read the latest list without it being a dep
    const studentResponseListRef = React.useRef(studentResponseList);
    React.useEffect(() => {
        studentResponseListRef.current = studentResponseList;
    }, [studentResponseList]);

    const handleStudentResponseListChange = React.useCallback(async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        const requestId = ++fetchRequestIdRef.current;
        validationRequestIdRef.current += 1;
        setStudentResponseList(value);
        const responseList = value.split("\n").map(line => line.trim()).filter(Boolean);

        validateStudentResponsePromptAndList(studentResponsePromptRef.current, responseList);

        try {
            const pinyinListList = await Promise.all(
                responseList.map(async (res) => {
                    const response = await SentenceApi.getTextPinyin(res);
                    return response.data.flat().join(" ");
                })
            );
            if (requestId !== fetchRequestIdRef.current) return;
            setStudentResponsePinyinList(pinyinListList.join("\n"));
            setStudentResponsePinyinErrorText(null);
        } catch (err) {
            if (requestId !== fetchRequestIdRef.current) return;
            handleErrorMessage(err, setErrorMessage, history, location);
            setStudentResponsePinyinErrorText("Unable to fetch pinyin");
        }
    }, [validateStudentResponsePromptAndList, setErrorMessage, history, location]);

    const handleStudentResponsePinyinListChange = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedPinyin = e.target.value;
        fetchRequestIdRef.current += 1;
        const requestId = ++validationRequestIdRef.current;
        setStudentResponsePinyinList(updatedPinyin);
        validatePinyin(studentResponseListRef.current, updatedPinyin, requestId)
    }, [validatePinyin]);

    const setStudentResponseFields = React.useCallback((studentResponsePrompt: string | null, dialog_prompt_sentence_list: IDialogPromptSentence[]) => {
        const prompt = studentResponsePrompt ? studentResponsePrompt : "";
        studentResponsePromptRef.current = prompt;
        setStudentResponsePrompt(prompt);

        const studentResponseList = dialog_prompt_sentence_list.map(item => item.student_response ? item.student_response : "")
        setStudentResponseList(studentResponseList.join("\n"));

        setStudentResponsePinyinList("");
        setStudentResponsePinyinErrorText(null);

        const loadNumericPinyin = async () => {
            const requestId = ++fetchRequestIdRef.current;
            validationRequestIdRef.current += 1;
            try {
                const pinyinList = await Promise.all(
                    studentResponseList.map(async (responseText) => {
                        if (!responseText.trim()) return "";
                        const response = await SentenceApi.getTextPinyin(responseText);
                        return response.data.flat().join(" ");
                    })
                );

                if (requestId !== fetchRequestIdRef.current) return;
                setStudentResponsePinyinList(pinyinList.join("\n"));
                const validationRequestId = ++validationRequestIdRef.current;
                validatePinyin(studentResponseList.join("\n"), pinyinList.join("\n"), validationRequestId);
            } catch (err) {
                if (requestId !== fetchRequestIdRef.current) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setStudentResponsePinyinErrorText("Unable to fetch pinyin");
            }
        };
        loadNumericPinyin();

    }, [validatePinyin, setErrorMessage, history, location]);

    const clearStudentResponseFields = React.useCallback(() => {
        studentResponsePromptRef.current = "<Answer/>";
        setStudentResponsePrompt("<Answer/>");
        setStudentResponseList("");
        setStudentResponsePinyinList("");
        setStudentResponsePromptErrorText(null)
        setStudentResponseListErrorText(null)
        setStudentResponsePinyinErrorText(null)
    }, []);

    const clearStudentResponseErrors = React.useCallback(() => {
        setStudentResponsePromptErrorText(null)
        setStudentResponseListErrorText(null)
        setStudentResponsePinyinErrorText(null)
    }, []);

    return {
        studentResponsePrompt,
        studentResponsePromptErrorText,
        studentResponseList,
        studentResponseListErrorText,
        studentResponsePinyinList,
        studentResponsePinyinErrorText,
        setStudentResponseFields,
        clearStudentResponseFields,
        clearStudentResponseErrors,
        handleStudentResponsePromptChange,
        handleStudentResponseListChange,
        handleStudentResponsePinyinListChange,
    };
}
